import { useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2, Copy, Loader2, Download, Trash2, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { QRCodeCanvas } from 'qrcode.react';
import { TopBanner } from '@/components/TopBanner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { apiClient } from '@/api/client';

export const Route = createFileRoute('/_authenticated/invitacion')({
  component: InvitacionPage,
});

interface InviteLink {
  id: string;
  code: string;
  url: string;
  label: string | null;
  isActive: boolean;
  count: number;
  createdAt: string;
}

const linksKey = ['invites', 'links'] as const;

function InvitacionPage() {
  const qc = useQueryClient();
  const [name, setName] = useState('');

  const { data: links, isLoading, isError, refetch } = useQuery({
    queryKey: linksKey,
    queryFn: async () => {
      const { data } = await apiClient.get('/invites/links');
      return data as InviteLink[];
    },
  });

  const createMut = useMutation({
    mutationFn: async (label: string) => {
      const { data } = await apiClient.post('/invites/links', { label });
      return data as InviteLink;
    },
    onSuccess: () => {
      setName('');
      toast.success('Link creado');
      void qc.invalidateQueries({ queryKey: linksKey });
    },
    onError: () => toast.error('No se pudo crear el link'),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/invites/links/${id}`);
    },
    onSuccess: () => {
      toast.success('Link borrado');
      void qc.invalidateQueries({ queryKey: linksKey });
    },
    onError: () => toast.error('No se pudo borrar el link'),
  });

  const handleCreate = () => {
    if (createMut.isPending) return;
    const label = name.trim();
    if (!label) {
      toast.error('Poné el nombre del jugador');
      return;
    }
    createMut.mutate(label);
  };

  return (
    <>
      <TopBanner title="Mi invitación" subtitle="Un link por cada jugador — sabés quién entró por cuál" />
      <div className="flex-1 overflow-auto p-4 lg:p-6">
        <div className="mx-auto max-w-2xl space-y-6">
          {/* Crear link por jugador */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Nuevo link para un jugador
              </CardTitle>
              <CardDescription>
                Poné el nombre (o teléfono) del jugador y generá su link. Es de un solo uso: se
                cierra apenas el jugador se registra, y lo ves en Jugadores.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  className="h-11 lg:h-10"
                  placeholder="Nombre del jugador (ej. Juan)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                  maxLength={80}
                />
                <Button
                  className="h-11 w-full shrink-0 sm:w-auto lg:h-10"
                  onClick={handleCreate}
                  disabled={createMut.isPending}
                >
                  {createMut.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="mr-2 h-4 w-4" />
                  )}
                  Generar link
                </Button>
              </div>
            </CardContent>
          </Card>

          {isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <p className="text-sm text-muted-foreground">No se pudieron cargar tus links.</p>
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                Reintentar
              </Button>
            </div>
          ) : (links?.length ?? 0) === 0 ? (
            <div className="flex flex-col items-center gap-1 py-10 text-center">
              <p className="text-sm font-medium">Todavía no generaste ningún link.</p>
              <p className="text-xs text-muted-foreground">
                Creá uno arriba con el nombre de tu primer jugador.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <h3 className="px-1 text-sm font-medium text-muted-foreground">
                Tus links ({links?.length ?? 0})
              </h3>
              {links?.map((l) => (
                <LinkRow
                  key={l.id}
                  link={l}
                  onDelete={() => deleteMut.mutate(l.id)}
                  deleting={deleteMut.isPending && deleteMut.variables === l.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function LinkRow({
  link,
  onDelete,
  deleting,
}: {
  link: InviteLink;
  onDelete: () => void;
  deleting: boolean;
}) {
  const qrRef = useRef<HTMLDivElement>(null);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      toast.success('¡Copiado!');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector('canvas') as HTMLCanvasElement | null;
    if (!canvas) {
      toast.error('No se pudo generar el QR');
      return;
    }
    try {
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `invitacion-${link.label ?? 'link'}-${link.code}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('QR descargado');
    } catch {
      toast.error('No se pudo descargar el QR');
    }
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 pt-6 sm:flex-row sm:items-center">
        {/* QR chico (también se usa para descargar) */}
        <div ref={qrRef} className="mx-auto shrink-0 sm:mx-0">
          <QRCodeCanvas value={link.url} size={72} level="M" includeMargin className="rounded border border-border" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate font-medium">{link.label ?? 'Sin nombre'}</span>
            {link.isActive ? (
              <span className="inline-flex shrink-0 items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                Sin usar
              </span>
            ) : (
              <span className="inline-flex shrink-0 items-center rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                Usado ✓
              </span>
            )}
          </div>
          <div className="mt-2 truncate font-mono text-xs text-muted-foreground">{link.url}</div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => void copy()} title="Copiar link">
            <Copy className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={downloadQR} title="Descargar QR">
            <Download className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onDelete}
            disabled={deleting}
            title="Borrar link"
            className="text-destructive hover:text-destructive"
          >
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
